/* eslint-disable */
"use client";
import * as React from "react";
import {
  Button,
  Flex,
  Grid,
  SwitchField,
  TextField,
} from "@aws-amplify/ui-react";
import { fetchByPath, getOverrideProps, validateField } from "./utils";
import { generateClient } from "aws-amplify/api";
import { getVariation } from "./graphql/queries";
import { updateVariation } from "./graphql/mutations";
const client = generateClient();
export default function VariationUpdateForm(props) {
  const {
    id: idProp,
    variation: variationModelProp,
    onSuccess,
    onError,
    onSubmit,
    onValidate,
    onChange,
    overrides,
    patterns,
    danceStyles,
    ...rest
  } = props;
  const initialValues = {
    status: false,
    order: "",
    rhytm: "",
    rhytmMan: "",
    hold: "",
    beats: "",
    level: "",
  };
  const [status, setStatus] = React.useState(initialValues.status);
  const [order, setOrder] = React.useState(initialValues.order);
  const [rhytm, setRhytm] = React.useState(initialValues.rhytm);
  const [rhytmMan, setRhytmMan] = React.useState(initialValues.rhytmMan);
  const [hold, setHold] = React.useState(initialValues.hold);
  const [beats, setBeats] = React.useState(initialValues.beats);
  const [level, setLevel] = React.useState(initialValues.level);
  const [errors, setErrors] = React.useState({});
  const resetStateValues = () => {
    const cleanValues = variationRecord
      ? { ...initialValues, ...variationRecord }
      : initialValues;
    setStatus(cleanValues.status);
    setOrder(cleanValues.order);
    setRhytm(cleanValues.rhytm);
    setRhytmMan(cleanValues.rhytmMan);
    setHold(cleanValues.hold);
    setBeats(cleanValues.beats);
    setLevel(cleanValues.level);
    setErrors({});
  };
  const [variationRecord, setVariationRecord] =
    React.useState(variationModelProp);
  React.useEffect(() => {
    const queryData = async () => {
      const record = idProp
        ? (
            await client.graphql({
              query: getVariation.replaceAll("__typename", ""),
              variables: { id: idProp },
            })
          )?.data?.getVariation
        : variationModelProp;
      setVariationRecord(record);
    };
    queryData();
  }, [idProp, variationModelProp]);
  React.useEffect(resetStateValues, [variationRecord]);
  const validations = {
    status: [],
    order: [],
    rhytm: [{ type: "Required" }],
    rhytmMan: [],
    hold: [{ type: "Required" }],
    beats: [{ type: "Required" }],
    level: [{ type: "Required" }],
  };
  const runValidationTasks = async (
    fieldName,
    currentValue,
    getDisplayValue
  ) => {
    const value =
      currentValue && getDisplayValue
        ? getDisplayValue(currentValue)
        : currentValue;
    let validationResponse = validateField(value, validations[fieldName]);
    const customValidator = fetchByPath(onValidate, fieldName);
    if (customValidator) {
      validationResponse = await customValidator(value, validationResponse);
    }
    setErrors((errors) => ({ ...errors, [fieldName]: validationResponse }));
    return validationResponse;
  };
  return (
    <Grid
      as="form"
      rowGap="15px"
      columnGap="15px"
      padding="20px"
      onSubmit={async (event) => {
        event.preventDefault();
        let modelFields = {
          status: status ?? null,
          order: order ?? null,
          rhytm,
          rhytmMan: rhytmMan ?? null,
          hold,
          beats,
          level,
        };
        const validationResponses = await Promise.all(
          Object.keys(validations).reduce((promises, fieldName) => {
            if (Array.isArray(modelFields[fieldName])) {
              promises.push(
                ...modelFields[fieldName].map((item) =>
                  runValidationTasks(fieldName, item)
                )
              );
              return promises;
            }
            promises.push(
              runValidationTasks(fieldName, modelFields[fieldName])
            );
            return promises;
          }, [])
        );
        if (validationResponses.some((r) => r.hasError)) {
          return;
        }
        if (onSubmit) {
          modelFields = onSubmit(modelFields);
        }
        try {
          Object.entries(modelFields).forEach(([key, value]) => {
            if (typeof value === "string" && value === "") {
              modelFields[key] = null;
            }
          });
          await client.graphql({
            query: updateVariation.replaceAll("__typename", ""),
            variables: {
              input: {
                id: variationRecord.id,
                ...modelFields,
              },
            },
          });
          if (onSuccess) {
            onSuccess(modelFields);
          }
        } catch (err) {
          if (onError) {
            const messages = err.errors.map((e) => e.message).join("\n");
            onError(modelFields, messages);
          }
        }
      }}
      {...getOverrideProps(overrides, "VariationUpdateForm")}
      {...rest}
    >
      <SwitchField
        label="Status"
        defaultChecked={false}
        isDisabled={false}
        isChecked={status}
        onChange={(e) => {
          let value = e.target.checked;
          if (onChange) {
            const modelFields = {
              status: value,
              order,
              rhytm,
              rhytmMan,
              hold,
              beats,
              level,
            };
            const result = onChange(modelFields);
            value = result?.status ?? value;
          }
          if (errors.status?.hasError) {
            runValidationTasks("status", value);
          }
          setStatus(value);
        }}
        onBlur={() => runValidationTasks("status", status)}
        errorMessage={errors.status?.errorMessage}
        hasError={errors.status?.hasError}
        {...getOverrideProps(overrides, "status")}
      ></SwitchField>
      <TextField
        label="Order"
        isRequired={false}
        isReadOnly={false}
        type="number"
        step="any"
        value={order}
        onChange={(e) => {
          let value = isNaN(parseInt(e.target.value))
            ? e.target.value
            : parseInt(e.target.value);
          if (onChange) {
            const modelFields = {
              status,
              order: value,
              rhytm,
              rhytmMan,
              hold,
              beats,
              level,
            };
            const result = onChange(modelFields);
            value = result?.order ?? value;
          }
          if (errors.order?.hasError) {
            runValidationTasks("order", value);
          }
          setOrder(value);
        }}
        onBlur={() => runValidationTasks("order", order)}
        errorMessage={errors.order?.errorMessage}
        hasError={errors.order?.hasError}
        {...getOverrideProps(overrides, "order")}
      ></TextField>
      <TextField
        label="Rhytm"
        isRequired={true}
        isReadOnly={false}
        value={rhytm}
        onChange={(e) => {
          let { value } = e.target;
          if (onChange) {
            const modelFields = {
              status,
              order,
              rhytm: value,
              rhytmMan,
              hold,
              beats,
              level,
            };
            const result = onChange(modelFields);
            value = result?.rhytm ?? value;
          }
          if (errors.rhytm?.hasError) {
            runValidationTasks("rhytm", value);
          }
          setRhytm(value);
        }}
        onBlur={() => runValidationTasks("rhytm", rhytm)}
        errorMessage={errors.rhytm?.errorMessage}
        hasError={errors.rhytm?.hasError}
        {...getOverrideProps(overrides, "rhytm")}
      ></TextField>
      <TextField
        label="Rhytm man"
        isRequired={false}
        isReadOnly={false}
        value={rhytmMan}
        onChange={(e) => {
          let { value } = e.target;
          if (onChange) {
            const modelFields = {
              status,
              order,
              rhytm,
              rhytmMan: value,
              hold,
              beats,
              level,
            };
            const result = onChange(modelFields);
            value = result?.rhytmMan ?? value;
          }
          if (errors.rhytmMan?.hasError) {
            runValidationTasks("rhytmMan", value);
          }
          setRhytmMan(value);
        }}
        onBlur={() => runValidationTasks("rhytmMan", rhytmMan)}
        errorMessage={errors.rhytmMan?.errorMessage}
        hasError={errors.rhytmMan?.hasError}
        {...getOverrideProps(overrides, "rhytmMan")}
      ></TextField>
      <TextField
        label="Hold"
        isRequired={true}
        isReadOnly={false}
        value={hold}
        onChange={(e) => {
          let { value } = e.target;
          if (onChange) {
            const modelFields = {
              status,
              order,
              rhytm,
              rhytmMan,
              hold: value,
              beats,
              level,
            };
            const result = onChange(modelFields);
            value = result?.hold ?? value;
          }
          if (errors.hold?.hasError) {
            runValidationTasks("hold", value);
          }
          setHold(value);
        }}
        onBlur={() => runValidationTasks("hold", hold)}
        errorMessage={errors.hold?.errorMessage}
        hasError={errors.hold?.hasError}
        {...getOverrideProps(overrides, "hold")}
      ></TextField>
      <TextField
        label="Beats"
        isRequired={true}
        isReadOnly={false}
        type="number"
        step="any"
        value={beats}
        onChange={(e) => {
          let value = isNaN(parseInt(e.target.value))
            ? e.target.value
            : parseInt(e.target.value);
          if (onChange) {
            const modelFields = {
              status,
              order,
              rhytm,
              rhytmMan,
              hold,
              beats: value,
              level,
            };
            const result = onChange(modelFields);
            value = result?.beats ?? value;
          }
          if (errors.beats?.hasError) {
            runValidationTasks("beats", value);
          }
          setBeats(value);
        }}
        onBlur={() => runValidationTasks("beats", beats)}
        errorMessage={errors.beats?.errorMessage}
        hasError={errors.beats?.hasError}
        {...getOverrideProps(overrides, "beats")}
      ></TextField>
      <TextField
        label="Level"
        isRequired={true}
        isReadOnly={false}
        value={level}
        onChange={(e) => {
          let { value } = e.target;
          if (onChange) {
            const modelFields = {
              status,
              order,
              rhytm,
              rhytmMan,
              hold,
              beats,
              level: value,
            };
            const result = onChange(modelFields);
            value = result?.level ?? value;
          }
          if (errors.level?.hasError) {
            runValidationTasks("level", value);
          }
          setLevel(value);
        }}
        onBlur={() => runValidationTasks("level", level)}
        errorMessage={errors.level?.errorMessage}
        hasError={errors.level?.hasError}
        {...getOverrideProps(overrides, "level")}
      ></TextField>
      <Flex
        justifyContent="space-between"
        {...getOverrideProps(overrides, "CTAFlex")}
      >
        <Button
          children="Reset"
          type="reset"
          onClick={(event) => {
            event.preventDefault();
            resetStateValues();
          }}
          isDisabled={!(idProp || variationModelProp)}
          {...getOverrideProps(overrides, "ResetButton")}
        ></Button>
        <Flex
          gap="15px"
          {...getOverrideProps(overrides, "RightAlignCTASubFlex")}
        >
          <Button
            children="Submit"
            type="submit"
            variation="primary"
            isDisabled={
              !(idProp || variationModelProp) ||
              Object.values(errors).some((e) => e?.hasError)
            }
            {...getOverrideProps(overrides, "SubmitButton")}
          ></Button>
        </Flex>
      </Flex>
    </Grid>
  );
}
